import React, { useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Paperclip, HardHat } from 'lucide-react';
import type { Appointment, TaskListItem, WorkOrderExternalSnapshot } from '@myapp/contract';
import {
  getAppointmentAttachmentTotal,
  getAppointmentStatusLabel,
} from '../../../entities/appointment';
import { getTaskStatusLabel, getTaskTypeLabel, useTaskDetail } from '../../../entities/task';
import { formatDateDDMMYYYY } from '../../../shared/helpers/date';
import { useClockMinute } from '../../../shared/hooks/useClockMinute';
import { formatTimeHHmm, resolveBlockTimes } from '../../../shared/lib/schedule';
import { BOARD_LAYER } from '../model/boardLayers';
import type { BoardBlock } from '../model/boardTypes';
import { buildReschedulingLine } from '../model/blockTooltip';

/**
 * The hover info card: everything a block is too small to say.
 *
 * A block is 60px tall and can be 20px wide. `SegmentCell` fits four truncated lines, so the Mieter,
 * the Schadensbeschreibung and the Ausführung-bis date — the three things a dispatcher actually
 * rings someone about — had nowhere to go but the native `title` attribute, where they were an
 * unstyled, unselectable blob with no clickable phone number. This is that content, rebuilt as the
 * popover the deleted `timeline-day` widget had (`AppointmentInfoPopover`), for Termin and Aufgabe.
 *
 * Read-only by design. It never opens a form and never writes: clicking a block already does that,
 * and a card that could act would turn a glance into a decision.
 */

const CARD_WIDTH = 320;
/** Also the card's `max-height` — `computePosition` needs a height before the card is measured. */
const CARD_MAX_HEIGHT = 420;
const MOBILE_BREAKPOINT = 540;
/** Clearance from the anchor and from the viewport edge, in px. */
const GAP = 8;

interface Position {
  top: number;
  left: number;
  width: number;
}

/**
 * Beside the block, flipped and clamped to stay on screen.
 *
 * `reservedRightPx` is the docked upsert panel. The card sits at `z-[90]` and the `Modal` shell at
 * 100, so a card placed under the panel would not merely overlap it — it would be invisible, with
 * the block still highlighted and nothing to show for it. Treating the panel as the right-hand edge
 * of the viewport makes the card flip to the left of the block instead.
 */
function computePosition(anchor: DOMRect, reservedRightPx: number): Position {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Narrow screens have no room beside anything: centre it and pin it under (or over) the block.
  if (vw < MOBILE_BREAKPOINT) {
    const margin = 12;
    const width = Math.min(CARD_WIDTH, vw - margin * 2);
    const left = Math.round((vw - width) / 2);

    let top = anchor.bottom + GAP;
    if (top + CARD_MAX_HEIGHT > vh - margin) top = anchor.top - CARD_MAX_HEIGHT - GAP;
    top = Math.max(margin, Math.min(top, vh - CARD_MAX_HEIGHT - margin));

    return { top, left, width };
  }

  const usableRight = vw - reservedRightPx;

  // Right of the block by preference — that is the direction the day runs, so the card covers hours
  // already read rather than the ones being looked at.
  let left = anchor.right + GAP;
  if (left + CARD_WIDTH > usableRight - GAP) left = anchor.left - CARD_WIDTH - GAP;
  left = Math.max(GAP, Math.min(left, usableRight - CARD_WIDTH - GAP));

  let top = anchor.top;
  if (top + CARD_MAX_HEIGHT > vh - GAP) top = vh - CARD_MAX_HEIGHT - GAP;
  top = Math.max(GAP, top);

  return { top, left, width: CARD_WIDTH };
}

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mt-2 border-t border-gray-100 pt-2 first:mt-0 first:border-t-0 first:pt-0">
    <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-400">{title}</div>
    {children}
  </div>
);

/** Renders nothing when it has nothing — that is what keeps empty sections off the card. */
const Row = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: string | null;
  children?: React.ReactNode;
}) => {
  if (!children && !value) return null;
  return (
    <div className="flex gap-2 text-sm text-gray-800">
      {/*
        `text-xs` in a 112px column, not `text-sm` in 96px: "Wirtschaftseinheit" is the longest label
        on the card and at body size it runs straight over its own value. `leading-5` keeps the
        smaller label on the value's baseline, and `break-words` is the backstop if a label ever
        grows past this column too.
      */}
      <span className="w-28 shrink-0 break-words text-xs leading-5 text-gray-500">{label}</span>
      <span className="min-w-0 select-text break-words">{children ?? value}</span>
    </div>
  );
};

const AttachmentLine = ({ count }: { count: number }) => {
  if (count <= 0) return null;
  return (
    <div className="mt-2 flex items-center gap-1 border-t border-gray-100 pt-2 text-[11px] text-gray-500">
      <Paperclip className="h-3 w-3" strokeWidth={2.5} />
      {count} {count === 1 ? 'Anhang' : 'Anhänge'}
    </div>
  );
};

/** The worker's files, named as such — a hard hat, not a second paperclip. */
const TechnicianAttachmentLine = ({ count }: { count: number }) => {
  if (count <= 0) return null;
  return (
    <div className="mt-2 flex items-center gap-1 border-t border-gray-100 pt-2 text-[11px] text-amber-700">
      <HardHat className="h-3 w-3" strokeWidth={2.5} />
      {count} {count === 1 ? 'Datei' : 'Dateien'} vom Handwerker
    </div>
  );
};

/** `08:15–10:45 · Geplant`, and under it the three durations the block splits into. */
const CardHeader = ({
  title,
  badge,
  timeRange,
  status,
  durations,
  reschedulingLine,
}: {
  title: string;
  badge?: string | null;
  timeRange: string;
  status: string;
  durations: string;
  reschedulingLine: string | null;
}) => (
  <>
    <div className="mb-1 flex items-start justify-between gap-2">
      <span className="select-text text-sm font-semibold text-gray-800">{title}</span>
      {badge && (
        <span className="shrink-0 rounded bg-[#124093] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          {badge}
        </span>
      )}
    </div>

    <div className="text-sm text-gray-800">
      {timeRange} · {status}
    </div>
    <div className="text-[11px] text-gray-500">{durations}</div>

    {reschedulingLine && (
      <div className="mt-1 rounded bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700">
        {reschedulingLine}
      </div>
    )}
  </>
);

const AppointmentBody = ({ appointment }: { appointment: Appointment }) => {
  const external = appointment.workOrder?.external;
  if (!external) return null;

  return (
    <>
      <GewerkSection external={external} />
      <LeistungSection external={external} />
      <AdresseSection external={external} />
      <MieterSection external={external} />
      <TermineSection external={external} />

      {(appointment.description || appointment.technicianComment) && (
        <Section title="Notizen">
          <Row label="Beschreibung" value={appointment.description} />
          <Row label="Handwerker" value={appointment.technicianComment} />
        </Section>
      )}
    </>
  );
};

const GewerkSection = ({ external }: { external: WorkOrderExternalSnapshot }) => {
  if (!external.gewerkeText && !external.handwerkerName) return null;
  return (
    <Section title="Gewerk">
      <Row label="Gewerk" value={external.gewerkeText} />
      <Row label="Handwerker" value={external.handwerkerName} />
    </Section>
  );
};

const LeistungSection = ({ external }: { external: WorkOrderExternalSnapshot }) => {
  // The Info-Text qualifies the Leistung rather than standing on its own — same concatenation the
  // deleted popover used, so the two read identically to anyone who knew the old one.
  const leistung = external.leistungText
    ? external.infoText
      ? `${external.leistungText} (${external.infoText})`
      : external.leistungText
    : null;

  if (!leistung && !external.individuellerText) return null;

  return (
    <>
      {leistung && (
        <Section title="Leistung">
          <div className="select-text whitespace-pre-wrap break-words text-sm text-gray-800">
            {leistung}
          </div>
        </Section>
      )}
      {external.individuellerText && (
        <Section title="Schadensbeschreibung">
          {/* The one field with no length limit — capped and scrolled so it cannot push the
              Mieter's phone number off the bottom of the card. */}
          <div
            className="select-text overflow-y-auto whitespace-pre-wrap break-words text-sm text-gray-800"
            style={{ maxHeight: 140 }}
          >
            {external.individuellerText}
          </div>
        </Section>
      )}
    </>
  );
};

const AdresseSection = ({ external }: { external: WorkOrderExternalSnapshot }) => {
  const veLine =
    external.ve != null
      ? external.veAdresse
        ? `${external.ve} · ${external.veAdresse}`
        : String(external.ve)
      : external.veAdresse;
  const wohnung = external.wohnung != null ? String(external.wohnung) : null;

  if (!veLine && !external.gebaeude && !external.hauseingang && !wohnung) return null;

  return (
    <Section title="Adresse">
      <Row label="Hauseingang" value={external.hauseingang} />
      <Row label="Wirtschaftseinheit" value={veLine} />
      <Row label="Gebäude" value={external.gebaeude} />
      <Row label="Wohnung" value={wohnung} />
    </Section>
  );
};

const MieterSection = ({ external }: { external: WorkOrderExternalSnapshot }) => {
  const { name, telefon, email } = external.mieter;
  if (!name && !telefon && !email) return null;

  return (
    <Section title="Mieter">
      <Row label="Name" value={name} />
      {telefon && (
        <Row label="Telefon">
          {/* The reason the card grants a grace period on close at all: a `title` tooltip cannot
              hold a link, and ringing the Mieter is the commonest thing to do from this card. */}
          <a href={`tel:${telefon.replace(/\s+/g, '')}`} className="text-blue-700 hover:underline">
            {telefon}
          </a>
        </Row>
      )}
      {email && (
        <Row label="E-Mail">
          <a href={`mailto:${email}`} className="break-all text-blue-700 hover:underline">
            {email}
          </a>
        </Row>
      )}
    </Section>
  );
};

const TermineSection = ({ external }: { external: WorkOrderExternalSnapshot }) => {
  if (!external.auftragsdatum && !external.ausfuehrungBis && !external.fertigmeldungsdat) {
    return null;
  }
  return (
    <Section title="Termine">
      <Row label="Auftragsdatum" value={formatDateDDMMYYYY(external.auftragsdatum)} />
      <Row label="Ausführung bis" value={formatDateDDMMYYYY(external.ausfuehrungBis)} />
      <Row label="Fertigmeldung" value={formatDateDDMMYYYY(external.fertigmeldungsdat)} />
    </Section>
  );
};

/**
 * The Aufgabe body, and the one place the card is not already holding everything it needs.
 *
 * The board is served by `TaskListItem`, which carries no `description` and no `attachments` — those
 * exist only on the detail shape. So this fetches them, and only while a card is open: React Query
 * caches under `taskKeys.detail(id)`, so hovering the same Aufgabe twice costs one request.
 *
 * Everything above the Beschreibung is already in hand, which is why there is no spinner here. A
 * loading or failed detail simply leaves those two rows out rather than blanking the card.
 */
const TaskBody = ({ task }: { task: TaskListItem }) => {
  const { data: detail } = useTaskDetail(task.id);

  return (
    <>
      <Section title="Details">
        <Row label="Art" value={getTaskTypeLabel(task.taskType)} />
        <Row label="Fällig" value={task.deadline ? formatDateDDMMYYYY(task.deadline) : null} />
        <Row label="Zugewiesen" value={task.assignedTechnician?.fullName} />
      </Section>

      {detail?.description && (
        <Section title="Beschreibung">
          <div
            className="select-text overflow-y-auto whitespace-pre-wrap break-words text-sm text-gray-800"
            style={{ maxHeight: 140 }}
          >
            {detail.description}
          </div>
        </Section>
      )}

      {task.technicianComment && (
        <Section title="Notizen">
          <Row label="Handwerker" value={task.technicianComment} />
        </Section>
      )}

      <AttachmentLine count={detail?.attachments?.length ?? 0} />
      <TechnicianAttachmentLine count={task.technicianAttachmentCount} />
    </>
  );
};

interface Props {
  block: BoardBlock;
  /** The block's box in viewport coordinates, from `useBlockHoverCard`. */
  anchorRect: DOMRect;
  /** Width of the docked upsert panel, or 0. See `computePosition`. */
  reservedRightPx: number;
  /** Keeps the card open while the pointer is inside it. */
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onClose: () => void;
}

export const BlockInfoCard = ({
  block,
  anchorRect,
  reservedRightPx,
  onPointerEnter,
  onPointerLeave,
  onClose,
}: Props) => {
  const { top, left, width } = useMemo(
    () => computePosition(anchorRect, reservedRightPx),
    [anchorRect, reservedRightPx],
  );
  const nowMs = useClockMinute();

  // Escape only. No outside-click handler on purpose: the card is dismissed by moving the pointer,
  // and a capture-phase `pointerdown` listener would swallow the first click of a drag on the board
  // underneath — `PausePopover` can afford one because it is modal and this is not.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const { entry, owner } = block;
  if (!owner || owner.kind === 'absence') return null;

  // Actuals win per component, exactly as the block's own segments read them. The owner status
  // decides whether an open phase is a session or a leftover — same rule as the block's ring.
  const ownerStatus = owner.kind === 'appointment' ? owner.appointment.status : owner.task.status;
  const times = resolveBlockTimes(entry, nowMs, ownerStatus);
  // To the live edge, like the block itself — a job still running at 11:40 is not `–11:00`.
  const timeRange = `${formatTimeHHmm(times.liveStartsAt)}–${formatTimeHHmm(times.liveEndsAt)}`;
  // Same wording as the block's own strips — a handover reads `Transfer` in both places.
  const travelLabel = block.segments.find((segment) => segment.kind === 'travel')?.label ?? 'Anfahrt';
  const departureLabel =
    block.segments.find((segment) => segment.kind === 'departure')?.label ?? 'Abfahrt';
  const durations = `${travelLabel} ${times.travelMinutes} · Arbeit ${times.workMinutes} · ${departureLabel} ${times.departureMinutes} min`;

  const reschedulingLine = entry.needsRescheduling ? buildReschedulingLine(entry) : null;

  const external = owner.kind === 'appointment' ? owner.appointment.workOrder?.external : undefined;

  const content = (
    <div
      className={`fixed overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ${BOARD_LAYER.hoverCard}`}
      style={{ top, left, width, maxHeight: CARD_MAX_HEIGHT }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      // The board reads `pointerdown` to start a drag and `click` to open a form. Selecting text in
      // here must do neither.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      // The block it describes is already announced by its own `aria-label`; a second reading of the
      // same job on hover would be noise.
      aria-hidden
    >
      <div className="overflow-y-auto px-4 py-3" style={{ maxHeight: CARD_MAX_HEIGHT }}>
        {owner.kind === 'appointment' ? (
          <>
            <CardHeader
              title={external ? `Nr. ${external.auftragJahr}/${external.auftragNr}` : 'Termin'}
              badge={external?.isWohnungsmodernisierung ? 'Modernisierung' : null}
              timeRange={timeRange}
              // The entity label already names a running phase ("Anfahrt läuft") — `deriveWorkState`.
              status={getAppointmentStatusLabel(owner.appointment.status, owner.appointment)}
              durations={durations}
              reschedulingLine={reschedulingLine}
            />
            <div className="mt-2">
              <AppointmentBody appointment={owner.appointment} />
            </div>
            <AttachmentLine count={getAppointmentAttachmentTotal(owner.appointment)} />
            <TechnicianAttachmentLine count={owner.appointment.technicianAttachments?.length ?? 0} />
            {external?.erfasserName && (
              <div className="mt-2 border-t border-gray-100 pt-2 text-[11px] text-gray-400">
                Erfasser: {external.erfasserName}
              </div>
            )}
          </>
        ) : (
          <>
            <CardHeader
              title={owner.task.title}
              timeRange={timeRange}
              status={getTaskStatusLabel(owner.task.status, owner.task)}
              durations={durations}
              reschedulingLine={reschedulingLine}
            />
            <div className="mt-2">
              <TaskBody task={owner.task} />
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Portaled, and not negotiable: the board's scroll container is `overflow-auto` and every block
  // carries `isolate` + `overflow-hidden`, so a card rendered in place would be clipped twice and
  // would scroll away with the grid. Same reasoning as the upsert modals being siblings of the
  // scroller — see `DispositionBoardWidget`.
  return ReactDOM.createPortal(content, document.body);
};
